import React from 'react';
import { useGetNpsCampaignResultsComments } from '@area-restrita/api-client/endpoints/nps/nps.gen';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { formatSubmittedDate, isForbiddenError } from '@/modules/nps/utils';

interface IProps {
  campaignId: string;
  canViewComments: boolean;
}

const COMMENTS_LIMIT = 25;

const COMMENT_CLASSIFICATION_LABELS: Record<string, string> = {
  promoter: 'Promotor',
  neutral: 'Neutro',
  detractor: 'Detrator',
};

const NpsResultsComments: React.FC<IProps> = ({
  campaignId,
  canViewComments,
}) => {
  const commentsQuery = useGetNpsCampaignResultsComments(
    campaignId,
    {
      id: campaignId,
      page: 1,
      limit: COMMENTS_LIMIT,
    },
    {
      query: {
        enabled: canViewComments,
      },
    },
  );

  if (!canViewComments || isForbiddenError(commentsQuery.error)) {
    return (
      <Alert>
        <AlertTitle>Comentários não disponíveis</AlertTitle>
        <AlertDescription>
          Os comentários desta campanha não estão disponíveis para o seu perfil.
        </AlertDescription>
      </Alert>
    );
  }

  if (commentsQuery.isLoading) {
    return (
      <div className="rounded-md border p-4 text-sm text-muted-foreground">
        Carregando comentários...
      </div>
    );
  }

  if (commentsQuery.isError) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Não foi possível carregar os comentários</AlertTitle>
        <AlertDescription>Tente novamente.</AlertDescription>
      </Alert>
    );
  }

  if (commentsQuery.data?.insufficientData) {
    return (
      <Alert>
        <AlertTitle>Comentários protegidos</AlertTitle>
        <AlertDescription>
          Os comentários são exibidos somente após a campanha receber pelo menos
          cinco respostas.
        </AlertDescription>
      </Alert>
    );
  }

  const comments = commentsQuery.data?.data ?? [];

  if (!comments.length) {
    return (
      <div className="rounded-md border p-4 text-sm text-muted-foreground">
        Nenhum comentário foi enviado nesta campanha.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {comments.map((comment) => (
        <article key={comment.id} className="rounded-md border p-4">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <Badge variant="secondary">Nota: {comment.score}</Badge>
            <Badge variant="outline">
              {COMMENT_CLASSIFICATION_LABELS[comment.classification] ??
                comment.classification}
            </Badge>
            <span className="text-sm text-muted-foreground">
              Enviado em {formatSubmittedDate(comment.submittedDate)}
            </span>
          </div>
          <p className="whitespace-pre-wrap text-sm">{comment.comment}</p>
        </article>
      ))}
    </div>
  );
};

export default NpsResultsComments;
